// Web Storage access that cannot throw.
//
// When a visitor blocks site data (Chrome/Firefox "block all cookies"), reading
// `window.localStorage` throws a SecurityError. `typeof localStorage` does NOT
// guard against this — `typeof` only suppresses ReferenceError for undeclared
// identifiers, so the throwing getter still runs.
//
// These wrappers stop OUR storage access from throwing. They do NOT make
// sign-in work for a visitor with site data blocked, and nothing in this file
// should be read as claiming they do: @auth0/auth0-spa-js writes the login
// transaction to sessionStorage inside `loginWithRedirect` itself (its
// TransactionManager defaults to SessionStorage unless `useCookiesForTransactions`
// is set), so the throw simply moves into the SDK. PKCE also cannot round-trip
// at all when the browser refuses to persist anything, so there is no
// client-side change that makes that visitor signable-in — the open question is
// what we show them instead of an endless spinner. Tracked separately.
//
// `document.cookie` needs no equivalent wrapper: it returns '' when blocked
// rather than throwing.

import * as Sentry from '@sentry/react';

type WebStorageKind = 'localStorage' | 'sessionStorage';

type StorageOp = 'read' | 'write' | 'remove' | 'keys' | 'clear';

// Report a swallowed failure, then stay quiet about that same failure.
//
// Silence is what let the original bug live: an unguarded read threw, a catch
// logged nothing, and a visitor sat on a blank page with nothing in Sentry.
// Swallowing the throw fixes the crash but keeps the invisibility, so record
// that it happened.
//
// Deduped per store AND per error name, not once globally. A full store in a
// perfectly normal browser raises QuotaExceededError; if that shared one flag
// with blocked site data, a single benign quota failure would permanently
// silence the SecurityError signal for that page load — and sizing the
// blocked-storage population is the entire reason this exists.
//
// Reported as tags, not `extra`: Sentry can only group and filter on tags,
// which is what turns this into a population count rather than a pile of
// individual events. Matches the pattern in BrandingProvider/FeatureFlagProvider.
//
// The key is deliberately NOT reported: some are namespaced with a customer id
// (`tach:headsUp:<customerId>`), and store + operation + error are enough.
const reported = new Set<string>();

const reportOnce = (kind: WebStorageKind, op: StorageOp, error: unknown) => {
  const errorName = error instanceof Error ? error.name : typeof error;
  const signal = `${kind}:${errorName}`;
  if (reported.has(signal)) return;
  reported.add(signal);
  try {
    Sentry.captureMessage(`Web Storage unavailable: ${kind} (${errorName})`, {
      level: 'warning',
      tags: {
        storage: kind,
        storage_operation: op,
        storage_error: errorName,
      },
    });
  } catch {
    // Reporting must never be the thing that breaks the page.
  }
};

const read = (kind: WebStorageKind, key: string): string | null => {
  try {
    return window[kind].getItem(key);
  } catch (error) {
    // Blocked site data — the caller treats a missing value the same way it
    // treats a first-ever visit, which is already a supported path.
    reportOnce(kind, 'read', error);
    return null;
  }
};

// Returns whether the value actually landed. Most callers do not care, but a
// caller that keeps an in-memory fallback (HeadsUp's session mirror) has to
// know the difference between "stored" and "silently dropped" — a void wrapper
// would tell it every write succeeded.
const write = (kind: WebStorageKind, key: string, value: string): boolean => {
  try {
    window[kind].setItem(key, value);
    return true;
  } catch (error) {
    // Blocked, or over quota (Safari private browsing). This is NOT harmless:
    // `tachUserRole` gates the /signup hold in AuthRouter and the
    // invite-backed role in UserContext, so a failed write here can bounce
    // someone out of onboarding. We cannot make the write succeed, but it
    // must not be invisible.
    reportOnce(kind, 'write', error);
    return false;
  }
};

const remove = (kind: WebStorageKind, key: string): void => {
  try {
    window[kind].removeItem(key);
  } catch (error) {
    // Nothing to clean up if we could never write it in the first place.
    reportOnce(kind, 'remove', error);
  }
};

const clear = (kind: WebStorageKind): void => {
  try {
    window[kind].clear();
  } catch (error) {
    // Nothing to clear if the store was never reachable.
    reportOnce(kind, 'clear', error);
  }
};

const keys = (kind: WebStorageKind): string[] => {
  try {
    return Object.keys(window[kind]);
  } catch (error) {
    reportOnce(kind, 'keys', error);
    return [];
  }
};

export const safeGetItem = (key: string): string | null =>
  read('localStorage', key);
export const safeSetItem = (key: string, value: string): void => {
  write('localStorage', key, value);
};
export const safeRemoveItem = (key: string): void =>
  remove('localStorage', key);

export const safeSessionGetItem = (key: string): string | null =>
  read('sessionStorage', key);
export const safeSessionSetItem = (key: string, value: string): void => {
  write('sessionStorage', key, value);
};

// Same write, but reports whether it landed — for callers that keep a fallback.
export const safeTrySetItem = (key: string, value: string): boolean =>
  write('localStorage', key, value);
export const safeSessionTrySetItem = (key: string, value: string): boolean =>
  write('sessionStorage', key, value);
export const safeSessionRemoveItem = (key: string): void =>
  remove('sessionStorage', key);

// Enumerating the store is just as unsafe as reading a key: `Object.keys(
// sessionStorage)` touches the same throwing accessor. Callers sweeping a key
// namespace get an empty list when storage is blocked, which is correct —
// there is nothing stored to sweep.
export const safeKeys = (): string[] => keys('localStorage');
export const safeSessionKeys = (): string[] => keys('sessionStorage');

export const safeClear = (): void => clear('localStorage');
export const safeSessionClear = (): void => clear('sessionStorage');

// Test seam only: reporting is once per signal per page load, which makes every
// test after the first blind to it. Not used by application code.
export const __resetStorageReportingForTests = () => {
  reported.clear();
};
